import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, CheckCircle2, Clock, Minus } from 'lucide-react';
import type { StepStatus } from '@/contexts/OnboardingContext';

interface CompletionScreenProps {
  stepStatuses: StepStatus[];
  onComplete: () => void;
  onNavigateToSettings: (tab: string) => void;
  onNavigateToPage: (page: string) => void;
}

interface StepSummaryRow {
  stepIndex: number;
  label: string;
  settingsTab: string;
}

const STEP_SUMMARY: StepSummaryRow[] = [
  { stepIndex: 1, label: 'Company Setup', settingsTab: 'integrations' },
  { stepIndex: 2, label: 'User Access', settingsTab: 'user-setup' },
  { stepIndex: 3, label: 'Booking Types', settingsTab: 'booking-type-setup' },
  { stepIndex: 4, label: 'Planner Defaults', settingsTab: 'settings' },
  { stepIndex: 5, label: 'Planner Groups', settingsTab: 'group-setup' },
];

const NEXT_STEPS = [
  'Check your resources have synced from Evo',
  'Create your first booking on the planner',
  'Assign resources to planner groups',
  'Review the utilisation heatmap',
  'Configure roles and permissions as more users are added',
];

function StatusIcon({ status }: { status: 'completed' | 'skipped' | 'not-configured' }) {
  if (status === 'completed') {
    return (
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
        <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={3} />
      </div>
    );
  }
  if (status === 'skipped') {
    return (
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100">
        <Clock className="h-3.5 w-3.5 text-amber-600" />
      </div>
    );
  }
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
      <Minus className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
  );
}

function StatusLabel({ status }: { status: 'completed' | 'skipped' | 'not-configured' }) {
  if (status === 'completed') {
    return <span className="text-xs font-medium text-green-600">Completed</span>;
  }
  if (status === 'skipped') {
    return <span className="text-xs font-medium text-amber-600">Skipped</span>;
  }
  return <span className="text-xs text-muted-foreground">Not configured</span>;
}

export function CompletionScreen({
  stepStatuses,
  onComplete,
  onNavigateToSettings,
  onNavigateToPage,
}: CompletionScreenProps) {
  const getStepStatus = (stepIndex: number): 'completed' | 'skipped' | 'not-configured' => {
    const s = stepStatuses.find((st) => st.stepIndex === stepIndex);
    if (!s) return 'not-configured';
    if (s.completed) return 'completed';
    if (s.skipped) return 'skipped';
    return 'not-configured';
  };

  const handleEdit = (tab: string) => {
    onNavigateToSettings(tab);
  };

  const handleGoToPlanner = () => {
    onNavigateToPage('calendar');
  };

  const handleGoToDashboard = () => {
    onNavigateToPage('overview');
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex flex-1 flex-col items-center justify-center p-8">
        <div className="w-full max-w-3xl space-y-8">
          {/* Success icon */}
          <div className="flex flex-col items-center gap-4 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}
            >
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">You're all set!</h2>
            <p className="text-sm text-muted-foreground">
              Your workspace is configured and ready to use.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-2 gap-6">
            {/* LEFT: Setup Summary */}
            <div className="rounded-lg border border-border bg-background p-5">
              <h3 className="mb-4 text-sm font-semibold text-foreground">Setup Summary</h3>
              <ul className="space-y-3">
                {STEP_SUMMARY.map((row) => {
                  const status = getStepStatus(row.stepIndex);
                  return (
                    <li
                      key={row.stepIndex}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <StatusIcon status={status} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {row.label}
                          </p>
                          <StatusLabel status={status} />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => handleEdit(row.settingsTab)}
                      >
                        Edit
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* RIGHT: What to do next */}
            <div className="rounded-lg border border-border bg-background p-5">
              <h3 className="mb-4 text-sm font-semibold text-foreground">What to do next</h3>
              <ul className="space-y-2.5">
                {NEXT_STEPS.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                      style={{ backgroundColor: '#1a8a8a' }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug">{step}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col items-center gap-3">
            <Button
              size="lg"
              onClick={handleGoToPlanner}
              className="px-8 text-white"
              style={{ backgroundColor: '#1a8a8a' }}
            >
              Go to Planner →
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              onClick={handleGoToDashboard}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
